import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { siteConfig, seoMeta } from '@/mocks/siteConfig';

export default function About() {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError('');

    const form = e.currentTarget;
    const honeypotEl = form.querySelector<HTMLInputElement>('[name="website_alt"]');
    if (honeypotEl && honeypotEl.value.trim() !== '') {
      setFormSubmitted(true);
      setFormLoading(false);
      return;
    }

    try {
      const formData = new FormData(form);
      const submitAddr = 'http://godhands.cn/';
      const response = await fetch(submitAddr, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      if (response.ok) {
        setFormSubmitted(true);
        form.reset();
      } else {
        let parsed;
        try { parsed = JSON.parse(responseText); } catch { /* ignore */ }
        setFormError(parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    } finally {
      setFormLoading(false);
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: seoMeta.about.title,
    description: seoMeta.about.description,
    url: `${siteConfig.url}/about`,
    isPartOf: { '@type': 'WebSite', name: siteConfig.name, url: siteConfig.url },
    about: {
      '@type': 'Organization',
      name: siteConfig.name,
      description: siteConfig.description,
      url: siteConfig.url,
      email: siteConfig.email,
      address: { '@type': 'PostalAddress', streetAddress: siteConfig.address },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: siteConfig.phone,
        email: siteConfig.email,
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
    },
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: siteConfig.name,
    url: siteConfig.url,
    description: siteConfig.description,
    email: siteConfig.email,
  };

  return (
    <>
      <SEOHead
        title={seoMeta.about.title}
        description={seoMeta.about.description}
        keywords={seoMeta.about.keywords}
        canonicalPath="/about"
        schemaJson={{ ...organizationSchema, ...aboutSchema }}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero */}
        <header className="relative bg-foreground-950 pt-20 pb-16 md:pt-24 md:pb-20 text-center overflow-hidden">
          <div className="absolute inset-0 overflow-hidden opacity-20">
            <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary-500/30 rounded-full blur-3xl"></div>
          </div>
          <div className="relative z-10 w-full max-w-3xl mx-auto px-4 md:px-6">
            <p className="text-primary-400 text-xs md:text-sm font-medium tracking-wider mb-3">关于平台</p>
            <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-4">
              致力于打造无障碍的<br />韩国漫画阅读平台
            </h1>
            <p className="text-foreground-400 text-sm md:text-base max-w-xl mx-auto mb-8">
              韩漫阁成立于2024年，是国内领先的韩国漫画在线阅读平台，为数百万漫画爱好者提供免费、高质量的漫画阅读服务
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a href="#contact" className="bg-primary-500 text-background-50 px-8 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                商务合作
              </a>
              <a href="#stats" className="border border-background-50/30 text-background-50 px-8 py-3 rounded-full text-sm font-medium hover:border-background-50/50 transition-colors cursor-pointer whitespace-nowrap">
                平台数据
              </a>
            </div>
          </div>
        </header>

        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        {/* Stats */}
        <section id="stats" className="py-10 bg-foreground-950">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              {[
                { icon: 'ri-book-open-line', label: '漫画总量', value: '10,000+' },
                { icon: 'ri-user-line', label: '活跃用户', value: '500万+' },
                { icon: 'ri-fire-line', label: '日更新量', value: '200+' },
                { icon: 'ri-star-line', label: '平均评分', value: '9.2' },
                { icon: 'ri-global-line', label: '覆盖地区', value: '30+' },
              ].map((stat) => (
                <div key={stat.label} className="flex flex-col items-center gap-2 p-4 bg-foreground-800/50 rounded-xl">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-foreground-800">
                    <i className={`${stat.icon} text-lg text-primary-400`}></i>
                  </div>
                  <span className="text-lg font-bold text-background-50">{stat.value}</span>
                  <span className="text-xs text-foreground-400">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="py-16 md:py-20 bg-secondary-100/50">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row items-start gap-8 mb-12">
              <div className="md:w-1/3">
                <p className="text-xs text-foreground-500 uppercase tracking-widest font-medium mb-2">{siteConfig.name}</p>
                <h2 className="font-heading text-foreground-950 text-2xl md:text-3xl font-bold">品牌故事与<br />使命愿景</h2>
              </div>
              <div className="md:w-2/3">
                <p className="text-foreground-600 text-sm md:text-base leading-relaxed">
                  韩漫阁诞生于对韩国漫画的热爱。我们致力于打造一个无障碍的漫画阅读环境，让每一位漫画爱好者都能随时随地享受高品质的韩漫内容。
                  平台汇聚了热血、恋爱、奇幻、都市等多种类型的优质韩国漫画，采用先进的下拉式阅读技术，为读者带来流畅舒适的阅读体验。
                  未来，我们将持续拓展漫画资源，优化阅读体验，努力成为中文世界最好的韩漫阅读平台。
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-foreground-950 rounded-xl p-6 text-background-50">
                <i className="ri-eye-line text-2xl text-primary-400 mb-4 block"></i>
                <h3 className="font-heading text-lg font-bold mb-2">我们的使命</h3>
                <p className="text-foreground-400 text-sm leading-relaxed">
                  打破阅读壁垒，让优质韩国漫画触手可及。我们致力于为全球中文读者提供最全面、最优质的韩漫阅读服务。
                </p>
              </div>
              <div className="relative rounded-xl overflow-hidden group">
                <img
                  src="https://readdy.ai/api/search-image?query=A%20creative%20team%20working%20together%20in%20a%20modern%20office%20with%20large%20monitors%20displaying%20colorful%20manhwa%20artwork%2C%20warm%20lighting%2C%20collaborative%20atmosphere%2C%20clean%20professional%20aesthetic&width=600&height=400&seq=about-team-work&orientation=landscape"
                  alt="韩漫阁团队"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <h3 className="font-heading text-lg font-bold text-background-50 mb-1">专业团队</h3>
                  <p className="text-background-50/70 text-xs">50+专业编辑与技术人员全天候服务</p>
                </div>
              </div>
              <div className="flex flex-col justify-between">
                <div>
                  <i className="ri-shield-check-line text-2xl text-accent-500 mb-4 block"></i>
                  <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">品质保障</h3>
                  <p className="text-foreground-500 text-sm leading-relaxed">
                    严格的漫画筛选机制，确保每部作品的高品质。专业翻译团队，保证阅读体验的原汁原味。
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <div className="flex flex-col md:flex-row gap-10 items-center">
              <div className="md:w-3/5">
                <p className="text-xs text-foreground-500 mb-2 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-foreground-950"></span>
                  用户口碑
                </p>
                <blockquote className="font-heading text-xl md:text-2xl text-foreground-950 leading-relaxed mb-4">
                  韩漫阁是我用过最好的韩漫阅读平台。界面简洁、加载流畅，而且真的是完全免费！漫画资源也很丰富，每天都有更新，强烈推荐给所有韩漫爱好者。
                </blockquote>
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-sm text-foreground-950">小猫咪爱吃鱼</span>
                  <span className="flex items-center gap-1 text-xs">
                    <i className="ri-star-fill text-accent-500"></i>
                    <span className="text-foreground-500">认证用户</span>
                  </span>
                </div>
              </div>
              <div className="md:w-2/5 flex justify-center">
                <div className="w-48 h-48 md:w-56 md:h-56 rounded-2xl overflow-hidden bg-secondary-100">
                  <img
                    src="https://readdy.ai/api/search-image?query=A%20young%20Asian%20woman%20reading%20manhwa%20on%20a%20tablet%2C%20black%20and%20white%20portrait%20photography%20style%2C%20side%20profile%2C%20looking%20slightly%20upward%2C%20clean%20studio%20lighting%2C%20minimalist%20composition%2C%20natural%20expression&width=500&height=500&seq=about-testimonial-user&orientation=squarish"
                    alt="用户评价"
                    className="w-full h-full object-cover object-top"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-20 bg-secondary-100/50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-12">常见问题</h2>
            <div className="flex flex-col gap-4">
              {[
                { q: '韩漫阁是否真的完全免费？', a: '是的，韩漫阁所有漫画内容均为免费阅读。我们通过广告合作维持运营，不会向用户收取任何费用。读者可以随时随地免费畅享海量韩国漫画。' },
                { q: '支持哪些设备阅读？', a: '韩漫阁采用响应式设计，完美适配电脑、平板和手机等各类设备。下拉式阅读体验针对手机竖屏进行了专门优化，让您在手机上也能享受流畅的阅读体验。' },
                { q: '漫画更新频率如何？', a: '我们每日更新大量漫画内容。热门连载作品保持与韩国同步更新，确保读者第一时间看到最新章节。平台目前日均更新超过200部漫画。' },
                { q: '如何联系我们进行商务合作？', a: '欢迎通过下方联系表单或直接发送邮件至 hello@godhands.cn 与我们取得联系。我们期待与版权方、广告主及其他合作伙伴展开各种形式的合作。' },
              ].map((faq, idx) => (
                <details key={idx} className="bg-background-50 rounded-xl p-5 border border-background-200/70 cursor-pointer group">
                  <summary className="font-medium text-foreground-950 text-sm list-none flex items-center justify-between">
                    {faq.q}
                    <i className="ri-add-line text-foreground-400 group-open:hidden"></i>
                    <i className="ri-subtract-line text-foreground-400 hidden group-open:block"></i>
                  </summary>
                  <p className="mt-3 text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-3xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center mb-4">商务合作 / 联系我们</h2>
            <p className="text-foreground-500 text-sm text-center mb-10">
              如有版权合作、广告投放或其他商务合作需求，请通过以下方式联系我们
            </p>

            {formSubmitted ? (
              <div className="bg-accent-50 border border-accent-200 rounded-xl p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-4">
                  <i className="ri-check-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="font-heading text-lg font-bold text-foreground-950 mb-2">提交成功！</h3>
                <p className="text-foreground-500 text-sm">感谢您的留言，我们将在1-3个工作日内回复您。</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-background-100 rounded-xl p-6 md:p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">姓名 *</label>
                    <input
                      type="text"
                      name="name"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-foreground-700 mb-1.5">邮箱 *</label>
                    <input
                      type="email"
                      name="email"
                      required
                      className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">合作类型</label>
                  <select
                    name="type"
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors cursor-pointer"
                  >
                    <option value="">请选择合作类型</option>
                    <option value="copyright">版权合作</option>
                    <option value="advertising">广告投放</option>
                    <option value="partnership">战略合作</option>
                    <option value="other">其他</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-medium text-foreground-700 mb-1.5">留言内容 *</label>
                  <textarea
                    name="message"
                    required
                    maxLength={500}
                    rows={4}
                    className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-background-200 text-sm text-foreground-950 outline-none focus:border-primary-400 transition-colors resize-none"
                    placeholder="请输入您的留言内容（500字以内）"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="form-honeypot"
                />

                {formError && (
                  <p className="text-red-500 text-xs mb-4">{formError}</p>
                )}

                <button
                  type="submit"
                  disabled={formLoading}
                  className="w-full bg-primary-500 text-background-50 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-60 whitespace-nowrap"
                >
                  {formLoading ? '提交中...' : '提交留言'}
                </button>
              </form>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-10">
              <div className="text-center p-5 bg-background-100 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-3">
                  <i className="ri-mail-line text-accent-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">电子邮箱</p>
                <p className="text-sm font-medium text-foreground-950">{siteConfig.email}</p>
              </div>
              <div className="text-center p-5 bg-background-100 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-3">
                  <i className="ri-phone-line text-accent-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">联系电话</p>
                <p className="text-sm font-medium text-foreground-950">{siteConfig.phone}</p>
              </div>
              <div className="text-center p-5 bg-background-100 rounded-xl">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 mx-auto mb-3">
                  <i className="ri-map-pin-line text-accent-600"></i>
                </div>
                <p className="text-xs text-foreground-500 mb-1">公司地址</p>
                <p className="text-sm font-medium text-foreground-950">{siteConfig.address}</p>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}